/**
 * Step 6: Live
 * Celebration moment. Campaign card with status, "share your ad" download,
 * and dashboard link. Uses SelfServeHeader + ShadCN Card, Badge, Progress, Separator.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import SelfServeHeader from '@/components/mediaplan/SelfServeHeader';
import ErrorCard from '@/components/mediaplan/ErrorCard';
import { DashboardSkeleton } from '@/components/mediaplan/ScreenSkeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  Download,
  ArrowRight,
  Calendar,
  MapPin,
  Users,
  Tv,
  Clock,
  Mail,
  BarChart3,
  Copy,
  Check,
} from 'lucide-react';
import mediaPlanApi from '@/services/mediaPlanApi';
import useMediaPlanFlow from '@/hooks/useMediaPlanFlow';

const EMAIL_TIMELINE = [
  { label: 'Confirmation email', time: 'Now', done: true },
  { label: 'First performance metrics', time: 'In 48 hours', done: false },
  { label: 'Weekly update', time: 'Every Monday', done: false },
  { label: 'Campaign ending notice', time: '3 days before end', done: false },
];

export default function CampaignLive() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const flow = useMediaPlanFlow();

  const campaignId = flow.campaignId || planId;

  const [campaign, setCampaign] = useState(flow.campaign || null);
  const [loading, setLoading] = useState(!flow.campaign);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Load campaign data
  useEffect(() => {
    if (campaign) return; // Already have data from flow context

    let cancelled = false;

    async function loadCampaign() {
      setLoading(true);
      setError(null);
      try {
        const data = await mediaPlanApi.getCampaign(campaignId);
        if (!cancelled) setCampaign(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load campaign');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCampaign();
    return () => { cancelled = true; };
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dynamic progress calculation
  const calculateProgress = () => {
    if (!campaign?.startDate || !campaign?.endDate) return { progress: 4, dayLabel: 'Day 1 of 28' };
    const start = new Date(campaign.startDate).getTime();
    const end = new Date(campaign.endDate).getTime();
    const now = Date.now();
    const totalDays = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    const elapsed = Math.max(0, Math.round((now - start) / (1000 * 60 * 60 * 24)));
    const currentDay = Math.min(elapsed + 1, totalDays);
    const progress = Math.min(100, Math.max(1, Math.round((elapsed / totalDays) * 100)));
    return { progress, dayLabel: `Day ${currentDay} of ${totalDays}` };
  };

  const { progress, dayLabel } = calculateProgress();
  const businessName = flow.business?.name || campaign?.businessName || 'Your Business';

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(`https://cornercast.tv/campaign/${planId}`);
    setLinkCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName={businessName} />
        <div className="max-w-xl mx-auto px-6 py-12">
          <DashboardSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName={businessName} />
        <ErrorCard
          message={error}
          onRetry={() => window.location.reload()}
          fallbackPath="/dashboard"
          fallbackLabel="Back to Dashboard"
        />
      </div>
    );
  }

  // Use campaign data for display, falling back to flow.plan for plan-level fields
  const plan = campaign || flow.plan || {};

  return (
    <div className="min-h-screen bg-background">
      <SelfServeHeader businessName={businessName} />

      <div className="max-w-xl mx-auto px-6 py-12">
        {/* Celebration */}
        <div className="text-center mb-10 animate-[fadeInUp_0.5s_ease_both]">
          <div className="relative inline-flex mb-6">
            <div className="w-20 h-20 rounded-full bg-teal/10 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-teal" />
            </div>
            <div className="absolute inset-0 rounded-full bg-teal/10 animate-ping" style={{ animationDuration: '2s' }} />
          </div>

          <h1 className="text-2xl font-bold tracking-tight mb-2">Your campaign is live!</h1>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
            Your ad is now running on premium streaming TV networks in your neighborhood.
            We'll keep you updated on performance.
          </p>
        </div>

        {/* Campaign card */}
        <Card className="mb-6 animate-[fadeInUp_0.5s_ease_both_0.1s]">
          <div className="px-5 py-3 bg-teal/5 border-b border-teal/10 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-teal animate-pulse" />
            <Badge variant="outline" className="text-teal-700 border-teal/30 text-[10px] uppercase tracking-wider font-semibold">
              Live
            </Badge>
            <span className="text-xs text-muted-foreground ml-auto">
              Started {new Date(plan.startDate || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>

          <CardContent className="pt-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-bold">{plan.name || 'Your Campaign'}</h3>
                <div className="text-sm text-muted-foreground">{businessName}</div>
              </div>
              <div className="text-xl font-bold">${(plan.budget || 0).toLocaleString()}</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 shrink-0" />
                <span>{plan.duration || '--'} -- ends {plan.endDate ? new Date(plan.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span>{plan.audience?.radius || '--'}mi radius</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-3.5 w-3.5 shrink-0" />
                <span>{(plan.audience?.households || 0).toLocaleString()} households</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Tv className="h-3.5 w-3.5 shrink-0" />
                <span>{plan.variants || 1} variant{(plan.variants || 1) > 1 ? 's' : ''} running</span>
              </div>
            </div>

            <Separator className="my-4" />
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-muted-foreground">Campaign progress</span>
              <span className="text-xs font-semibold">{dayLabel}</span>
            </div>
            <Progress
              value={progress}
              className="h-1.5 [&>[data-slot=progress-indicator]]:bg-gradient-to-r [&>[data-slot=progress-indicator]]:from-coral [&>[data-slot=progress-indicator]]:to-teal"
            />
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex gap-3 mb-8 animate-[fadeInUp_0.5s_ease_both_0.2s]">
          <Button variant="outline" className="flex-1 gap-1.5" onClick={handleCopyLink}>
            {linkCopied ? <Check className="h-4 w-4 text-teal" /> : <Copy className="h-4 w-4" />}
            {linkCopied ? 'Link copied' : 'Share campaign link'}
          </Button>
          <Button variant="outline" className="flex-1 gap-1.5">
            <Download className="h-4 w-4" />
            Download ad
          </Button>
        </div>

        {/* Email timeline */}
        <Card className="mb-8 animate-[fadeInUp_0.5s_ease_both_0.3s]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Email updates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {EMAIL_TIMELINE.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center shrink-0',
                  item.done ? 'bg-teal/10' : 'bg-muted'
                )}>
                  {item.done ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-teal" />
                  ) : (
                    <Clock className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 flex items-center justify-between">
                  <span className={cn('text-sm', item.done ? 'font-medium' : 'text-muted-foreground')}>{item.label}</span>
                  <span className="text-xs text-muted-foreground">{item.time}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Next steps */}
        <div className="flex flex-col gap-3 animate-[fadeInUp_0.5s_ease_both_0.4s]">
          <Button
            className="w-full bg-coral hover:bg-coral/90 text-white shadow-[0_2px_10px_rgba(255,107,107,0.35)] h-11 gap-2"
            onClick={() => navigate('/dashboard')}
          >
            <BarChart3 className="h-4 w-4" />
            View Dashboard
          </Button>
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => navigate('/campaign/new')}
          >
            Start a New Campaign
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
